/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { Cell } from '@lexical/table';
import type { LexicalEditor } from 'lexical';

import './TableCellResizer.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  getCellFromTarget,
} from '@lexical/table';
import { $findMatchingParent } from '@lexical/utils';
import {
  $getNearestNodeFromDOMNode,
  $getSelection,
  DEPRECATED_$isGridSelection,
  COMMAND_PRIORITY_HIGH,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import * as React from 'react';
import {
  ReactPortal,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  containerContentWidth,
  resizeTableCells,
  tableAlignOf,
} from '../utils/tableWidth';
import { $readTableCells } from '../utils/tableWidthLexical';

type MousePosition = {
  x: number;
  y: number;
};

type MouseDraggingDirection = 'right' | 'bottom';

const MIN_ROW_HEIGHT = 33;

function TableCellResizer({ editor }: { editor: LexicalEditor }): JSX.Element {
  const targetRef = useRef<HTMLElement | null>(null);
  const resizerRef = useRef<HTMLDivElement | null>(null);
  const tableRectRef = useRef<ClientRect | null>(null);

  const mouseStartPosRef = useRef<MousePosition | null>(null);
  const [mouseCurrentPos, updateMouseCurrentPos] =
    useState<MousePosition | null>(null);

  const [activeCell, updateActiveCell] = useState<Cell | null>(null);
  const [isSelectingGrid, updateIsSelectingGrid] = useState<boolean>(false);
  const [draggingDirection, updateDraggingDirection] =
    useState<MouseDraggingDirection | null>(null);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (payload) => {
        const selection = $getSelection();
        const isGridSelection = DEPRECATED_$isGridSelection(selection);

        if (isSelectingGrid !== isGridSelection) {
          updateIsSelectingGrid(isGridSelection);
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  });

  const resetState = useCallback(() => {
    updateActiveCell(null);
    targetRef.current = null;
    updateDraggingDirection(null);
    mouseStartPosRef.current = null;
    tableRectRef.current = null;
  }, []);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      setTimeout(() => {
        const target = event.target;

        if (draggingDirection) {
          updateMouseCurrentPos({
            x: event.clientX,
            y: event.clientY,
          });
          return;
        }

        if (resizerRef.current && resizerRef.current.contains(target as Node)) {
          return;
        }

        if (targetRef.current !== target) {
          targetRef.current = target as HTMLElement;
          const cell = getCellFromTarget(target as HTMLElement);

          /**
           * **남의 편집기 표는 맡지 않는다** (2026-09-07, 오너 보고 「표 수정하려고 하면 에러가 뜬다」).
           *
           * 이 mousemove 는 `document` 에 걸려 있어 **화면의 모든 편집기가 같은 이벤트를 받는다.**
           * 문제 편집 모달에는 편집기가 둘이다(실측: 본문·풀이 — ContentEditable__root 2개). 표가 본문에만
           * 있으면, 풀이 쪽 TableCellResizer 도 그 셀을 target 으로 받아 제 editor 에서 노드를 찾다가
           * 반드시 실패한다($getNearestNodeFromDOMNode 는 그 editor 가 그린 DOM 만 안다). 그 throw 를
           * LexicalComposer 의 onError(EditorComposer.tsx:22)가 **다시 던져** Uncaught 로 번지고,
           * 개발 서버에서는 화면을 통째로 덮는 오류 창이 떠 작업이 멈춘다.
           *
           * 그래서 **내 root 안의 셀만** 맡는다. 밖이면 내 상태를 접는다 — 남의 표로 마우스가 옮겨갔는데
           * 내 손잡이가 남아 있으면, 그 표에 걸리지도 않는 손잡이가 떠 있는 셈이 된다.
           */
          const rootElement = editor.getRootElement();
          const isOwnCell = !!cell && !!rootElement && rootElement.contains(cell.elem);

          if (cell && isOwnCell && activeCell !== cell) {
            editor.update(() => {
              const tableCellNode = $getNearestNodeFromDOMNode(cell.elem);
              /**
               * 여기까지 왔는데 노드가 없다면 **DOM 과 편집기 상태가 아직 안 맞은 순간**이다(표를 막 넣은 뒤,
               * 편집기가 다시 그려지는 중 등). 종전에는 throw 라 그 찰나가 화면 전체를 죽였다.
               * 손잡이가 한 번 안 뜨는 것과 편집기가 죽는 것 중 앞을 고른다 — 다음 mousemove 에 다시 잡는다.
               */
              if (!tableCellNode) {
                resetState();
                return;
              }

              const tableNode =
                $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
              const tableElement = editor.getElementByKey(tableNode.getKey());

              if (!tableElement) {
                resetState();
                return;
              }

              targetRef.current = target as HTMLElement;
              tableRectRef.current = tableElement.getBoundingClientRect();
              updateActiveCell(cell);
            });
          } else if (!isOwnCell) {
            resetState();
          }
        }
      }, 0);
    };

    document.addEventListener('mousemove', onMouseMove);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [activeCell, draggingDirection, editor, resetState]);

  const isHeightChanging = (direction: MouseDraggingDirection) => {
    if (direction === 'bottom') return true;
    return false;
  };

  const updateRowHeight = useCallback(
    (newHeight: number) => {
      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      editor.update(() => {
        const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
        if (!$isTableCellNode(tableCellNode)) {
          throw new Error('TableCellResizer: Table cell node not found.');
        }

        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

        const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

        const tableRows = tableNode.getChildren();

        if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
          throw new Error('Expected table cell to be inside of table row.');
        }

        const tableRow = tableRows[tableRowIndex];

        if (!$isTableRowNode(tableRow)) {
          throw new Error('Expected table row');
        }

        tableRow.setHeight(newHeight);
      });
    },
    [activeCell, editor]
  );

  /**
   * 활성 칸의 **오른쪽 선**을 deltaPx 만큼 옮긴다 — 규칙은 utils/tableWidth.ts(오너 결정 2026-10-06).
   *   안쪽 선 = 양옆 두 열만 · 바깥 선(마지막 열) = 표 폭.
   * 모든 칸에 폭을 한꺼번에 쓴다(합친 칸은 덮는 열의 합). 일부 열만 쓰면 테마의 100% 가 나머지 열을
   * 늘려 끈 선이 되돌아갔다(종전 결함). 폭은 기준 폭 423 위의 값으로 저장한다 — 편집기 폭이 달라도
   * (칸 경계를 끈 문제 보기, OCR 검수, 2단 칸 안) 종이에서 같은 비율로 선다.
   * 구조가 기대와 다르면 던지지 않고 그만둔다 — 던지면 LexicalComposer 의 onError 가 화면을 죽인다.
   */
  const resizeColumnAtBoundary = useCallback(
    (deltaPx: number) => {
      if (!activeCell) return;
      editor.update(() => {
        const cellNode = $getNearestNodeFromDOMNode(activeCell.elem);
        if (!$isTableCellNode(cellNode)) return;
        const tableNode = $findMatchingParent(cellNode, $isTableNode);
        if (!$isTableNode(tableNode)) return;
        const tableElement = editor.getElementByKey(tableNode.getKey());
        const containerWidth = tableElement
          ? containerContentWidth(tableElement)
          : 0;
        if (!(containerWidth > 0)) return;

        const rows = $readTableCells(tableNode);
        let active: { row: number; index: number } | null = null;
        for (let r = 0; r < rows.length && !active; r += 1) {
          const index = rows[r].findIndex((c) => c.node.is(cellNode));
          if (index >= 0) active = { row: r, index };
        }
        if (!active) return;

        // 지금 그려진 칸 경계 — 끌지 않은 표를 처음 고정할 때, 합친 칸만 덮는 열을 잴 때 쓴다
        const edges = rows.map((cells) =>
          cells.map((c) => {
            const el = editor.getElementByKey(c.node.getKey());
            if (!el) return null;
            const rect = el.getBoundingClientRect();
            return { left: rect.left, right: rect.right };
          })
        );
        const widths = resizeTableCells({
          rows,
          edges,
          containerWidth,
          active,
          deltaPx,
          align: tableAlignOf(tableNode.getFormatType()),
        });
        if (!widths) return;
        rows.forEach((cells, r) =>
          cells.forEach((c, i) => {
            const w = widths[r][i];
            if (w > 0 && c.node.getWidth() !== w) c.node.setWidth(w);
          })
        );
      });
    },
    [activeCell, editor]
  );

  const toggleResize = useCallback(
    (direction) => (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!activeCell) {
        throw new Error('TableCellResizer: Expected active cell.');
      }

      if (draggingDirection === direction && mouseStartPosRef.current) {
        const { x, y } = mouseStartPosRef.current;

        if (!activeCell) {
          return;
        }

        const { height } = activeCell.elem.getBoundingClientRect();

        if (isHeightChanging(direction)) {
          const heightChange = Math.abs(event.clientY - y);

          const isShrinking = direction === 'bottom' && y > event.clientY;

          updateRowHeight(
            Math.max(
              isShrinking ? height - heightChange : heightChange + height,
              MIN_ROW_HEIGHT
            )
          );
        } else {
          resizeColumnAtBoundary(event.clientX - x);
        }

        resetState();
      } else {
        mouseStartPosRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
        updateMouseCurrentPos(mouseStartPosRef.current);
        updateDraggingDirection(direction);
      }
    },
    [
      activeCell,
      draggingDirection,
      resetState,
      resizeColumnAtBoundary,
      updateRowHeight,
    ]
  );

  const getResizers = useCallback(() => {
    if (activeCell) {
      const { height, width, top, left } =
        activeCell.elem.getBoundingClientRect();

      const styles = {
        bottom: {
          backgroundColor: 'none',
          cursor: 'row-resize',
          height: '10px',
          left: `${window.pageXOffset + left}px`,
          top: `${window.pageYOffset + top + height}px`,
          width: `${width}px`,
        },
        right: {
          backgroundColor: 'none',
          cursor: 'col-resize',
          height: `${height}px`,
          left: `${window.pageXOffset + left + width}px`,
          top: `${window.pageYOffset + top}px`,
          width: '10px',
        },
      };

      const tableRect = tableRectRef.current;

      if (draggingDirection && mouseCurrentPos && tableRect) {
        if (isHeightChanging(draggingDirection)) {
          styles[draggingDirection].left = `${
            window.pageXOffset + tableRect.left
          }px`;
          styles[draggingDirection].top = `${
            window.pageYOffset + mouseCurrentPos.y
          }px`;
          styles[draggingDirection].height = '3px';
          styles[draggingDirection].width = `${tableRect.width}px`;
        } else {
          styles[draggingDirection].top = `${
            window.pageYOffset + tableRect.top
          }px`;
          styles[draggingDirection].left = `${
            window.pageXOffset + mouseCurrentPos.x
          }px`;
          styles[draggingDirection].width = '3px';
          styles[draggingDirection].height = `${tableRect.height}px`;
        }

        styles[draggingDirection].backgroundColor = '#adf';
      }

      return styles;
    }

    return {
      bottom: null,
      left: null,
      right: null,
      top: null,
    };
  }, [activeCell, draggingDirection, mouseCurrentPos]);

  const resizerStyles = getResizers();

  return (
    <div ref={resizerRef} className="TableCellResizer__layer">
      {activeCell != null && !isSelectingGrid && (
        <>
          <div
            className="TableCellResizer__resizer TableCellResizer__ui"
            style={resizerStyles.right}
            onMouseDown={toggleResize('right')}
            onMouseUp={toggleResize('right')}
          />
          <div
            className="TableCellResizer__resizer TableCellResizer__ui"
            style={resizerStyles.bottom}
            onMouseDown={toggleResize('bottom')}
            onMouseUp={toggleResize('bottom')}
          />
        </>
      )}
    </div>
  );
}

export default function TableCellResizerPlugin(): ReactPortal {
  const [editor] = useLexicalComposerContext();

  return useMemo(
    () => createPortal(<TableCellResizer editor={editor} />, document.body),
    [editor]
  );
}
